import React from "react";

export function useAutoScroll(containerRef: React.RefObject<HTMLDivElement>) {
  const [atBottom, setAtBottom] = React.useState(true);

  const recalc = React.useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight <= 2;
    setAtBottom(isAtBottom);
  }, [containerRef]);

  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // initial compute
    recalc();
    el.addEventListener("scroll", recalc);
    return () => el.removeEventListener("scroll", recalc);
  }, [containerRef, recalc]);

  const scrollToBottom = React.useCallback(
    (smooth = true) => {
      const el = containerRef.current;
      if (!el) return;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: smooth ? "smooth" : "auto",
      });
      // after programmatic scroll, recompute on next frame so state matches DOM
      requestAnimationFrame(recalc);
    },
    [containerRef, recalc],
  );

  // Call this after you clear/start a new chat to ensure arrow hides
  const resetAutoScroll = React.useCallback(() => {
    // wait a frame so the DOM reflects cleared messages
    requestAnimationFrame(() => {
      // with an empty chat, we want atBottom = true (arrow hidden)
      recalc();
    });
  }, [recalc]);

  return { atBottom, scrollToBottom, recalc, resetAutoScroll };
}
